import Label from "../../../../shared/form/Label";
import ActorImage from "../../ActorImage";
import ActorInfo from "../../ActorInfo";
import ActivityObject from "../../ActivityObject";
import { groupBy, isEmpty } from "lodash";
import ImageAttachments from "../../object/attachments/ImageAttachments";
import Mentions from "../../object/attachments/Mentions";
import LinkAttachments from "../../object/attachments/LinkAttachments";
import FileAttachments from "../../object/attachments/FileAttachments";
import TagAttachments from "../../../attachments/TagAttachments";
import StaticTimeAgo from "../../../../shared/StaticTimeAgo";
import {
  Activity,
  ArticleObject,
  FileAttachment,
  ImageAttachment,
  LinkAttachment,
  ObjectWithContent,
  PeopleAttachment,
} from "../../../../../@types/activities";

type SharedActivityArgs = Omit<Activity, "shared_from" | "object"> & {
  translatedContent?: {
    language?: string;
    content?: string;
    name?: string;
    description?: string;
  };
  object: ObjectWithContent | ArticleObject;
  readOnly?: boolean;
};

export default function SharedActivity({
  id,
  actor,
  object,
  published,
  translatedContent,
  readOnly = false,
}: SharedActivityArgs) {
  const { attachments, tags } = object;

  const groupedAttachments = groupBy(attachments, "type");

  const objectArgs =
    object.type !== "article"
      ? { content: translatedContent?.content || object.content }
      : {};

  return (
    <div>
      <Label label={I18n.t("js.activitystreams.shared_from.label")} />
      <div className="activity my-2">
        <div className="activity-header flex mb-2">
          <div className="activity-actor-image py-4 mr-4 shrink-0">
            <ActorImage {...actor} />
          </div>
          <div className="activity-headline py-4 flex-1 flex flex-col gap-2">
            <ActorInfo {...actor} />
            {published && (
              <div className="flex leading-none text-sm text-muted gap-1 -mt-1">
                <a
                  className="text-muted text-sm leading-none"
                  href={`/activities/${id}`}
                >
                  <StaticTimeAgo date={published} />
                </a>
              </div>
            )}
          </div>
        </div>
        <div className="activity-body">
          <ActivityObject
            activityId={object.id}
            {...object}
            {...objectArgs}
            translated={!!translatedContent?.language}
            readOnly={readOnly}
          />
          <div className="mt-6 flex flex-col gap-2 divide-y divide-dashed divide-neutral *:pt-3">
            {!isEmpty(groupedAttachments.image) && (
              <ImageAttachments
                images={groupedAttachments.image as ImageAttachment[]}
              />
            )}
            {!isEmpty(groupedAttachments.person) && (
              <Mentions
                mentions={groupedAttachments.person as PeopleAttachment[]}
              />
            )}
            {!isEmpty(groupedAttachments.link) && (
              <LinkAttachments
                links={groupedAttachments.link as LinkAttachment[]}
              />
            )}
            {!isEmpty(groupedAttachments.file) && (
              <FileAttachments
                files={groupedAttachments.file as FileAttachment[]}
              />
            )}
            {!isEmpty(tags) && tags && <TagAttachments tags={tags} />}
          </div>
        </div>
      </div>
    </div>
  );
}
